// Read-only rows for an optimistic (receipt-confirmed, not-yet-reconciled)
// trade. No click handlers, no buttons — these mirror server rows visually
// once reconciliation swaps them out, but never act as real interactive rows.
import { CurrencyView } from '../currency-view';
import { FormatAfterDate } from '../format/format-after-date';
import { PendingTrade, PendingTradeStatus } from '@/client/store/pending-trades-store';
import { cn } from '@/client/utils';
import { useTranslation } from 'react-i18next';

export const PendingStatusDot = ({ status }: { status: PendingTradeStatus }) => {
  return (
    <span
      className={cn(
        'inline-block size-2 rounded-full flex-shrink-0',
        status === 'pending' ? 'bg-amber-500 animate-pulse' : 'bg-foreground/40'
      )}
    />
  );
};

// Shared status text — pending shows a spinner-adjacent "syncing", syncSlow
// (90s timeout hit) reassures the user their funds are safe without an error tone.
const SyncStatusText = ({ status }: { status: PendingTradeStatus }) => {
  const { t } = useTranslation();
  return (
    <span className="text-xs text-foreground/70 whitespace-nowrap">
      {status === 'pending'
        ? t('syncPending', 'Syncing...')
        : t('syncSlow', 'Sync delayed - confirmed on-chain, will appear shortly')}
    </span>
  );
};

// Buy = base token decimal (raw units in); sell = option share decimal (raw
// units in). Falls back to the base decimal when optionDecimal wasn't known
// at submit time.
const amountDecimalFor = (trade: PendingTrade): number =>
  trade.side === 'buy' ? trade.decimal : (trade.optionDecimal ?? trade.decimal);

type PendingRowProps = { trade: PendingTrade };
type PendingTradeFeedRowProps = PendingRowProps & { className?: string };

// Positions-tab row — shell matches PositionsItem (border/rounded/bg-card,
// same padding) but is not clickable and carries no action buttons.
export const PendingPositionRow = ({ trade }: PendingRowProps) => {
  const { t } = useTranslation();
  return (
    <div className="py-3 ps-4 pe-3 border border-foreground/10 rounded-4 bg-card">
      <div className="space-y-1">
        <div className="text-sm line-clamp-1 text-start leading-4.25 font-medium text-foreground">
          {trade.marketName}
          {trade.optionName && <span className="text-foreground/70"> · {trade.optionName}</span>}
        </div>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1.5">
            <span className="text-xs text-foreground/70">{trade.side === 'buy' ? t('buy') : t('sell')}</span>
            <CurrencyView
              value={trade.amountIn}
              decimal={amountDecimalFor(trade)}
              precision={2}
              baseTokenAddress={trade.baseTokenAddress}
              className="text-sm font-medium text-foreground"
            />
          </div>
          <div className="flex items-center gap-1.5">
            <PendingStatusDot status={trade.status} />
            <SyncStatusText status={trade.status} />
          </div>
        </div>
      </div>
    </div>
  );
};

// Trade-feed row (history tab / event trades) — layout aligned with the
// EventTrades row (p-4 text-sm flex justify-between). No fixed min-width by
// default (fits the wallet modal's narrow ~407px sidebar); callers that lay
// out a wide, horizontally-scrollable table (e.g. EventTrades) pass their own
// min-w-* via className to match neighboring rows.
export const PendingTradeFeedRow = ({ trade, className }: PendingTradeFeedRowProps) => {
  const { t } = useTranslation();
  return (
    <div className={cn('w-full p-4 text-sm leading-[normal] flex items-center justify-between gap-4 font-semibold', className)}>
      <div className="flex-1 gap-2 flex items-center">
        <span className="text-foreground/70">{trade.side === 'buy' ? t('buy') : t('sell')}</span>
        {trade.marketName && <span className="text-foreground line-clamp-1 max-w-24 md:max-w-40">{trade.marketName}</span>}
        {trade.optionName && <span className="text-foreground/70 line-clamp-1">{trade.optionName}</span>}
        <CurrencyView
          className="font-normal"
          showIcon
          value={trade.amountIn}
          decimal={amountDecimalFor(trade)}
          baseTokenAddress={trade.baseTokenAddress}
          precision={2}
        />
      </div>
      <div className="flex items-center gap-1.5 text-foreground/70 flex-shrink-0">
        <FormatAfterDate time={new Date(trade.createdAt)} />
        <PendingStatusDot status={trade.status} />
        <SyncStatusText status={trade.status} />
      </div>
    </div>
  );
};
